import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  AppBar,
  Toolbar,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Drawer,
  IconButton,
  Collapse,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ForumIcon from "@mui/icons-material/Forum";
import ReportIcon from "@mui/icons-material/Report";
import BlockIcon from "@mui/icons-material/Block";
import RouterIcon from "@mui/icons-material/Router";
import LanguageIcon from "@mui/icons-material/Language";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MessageIcon from "@mui/icons-material/Message";
import TuneIcon from "@mui/icons-material/Tune";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import StorageIcon from "@mui/icons-material/Storage";
import BarChartIcon from "@mui/icons-material/BarChart";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import ArticleIcon from "@mui/icons-material/Article";
import ImageIcon from "@mui/icons-material/Image";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  ChatGuestsSection,
  ChatUserDetailSection,
  ChatRoomsSection,
  ChatMessagesSection,
  UsernameWordsSection,
  ChatSettingsSection,
  DatabaseSection,
  ChatroomOverviewSection,
  ChatIPAddressesSection,
  ChatIPUsersSection,
  ChatFingerprintingSection,
  ChatReportsSection,
  BlogSection,
  MediaSection,
  UsersGuestsSection,
} from "../components/admin";

axios.defaults.withCredentials = true;

const drawerWidth = 240;

// Set admin page title
const useAdminTitle = () => {
  useEffect(() => {
    const originalTitle = document.title;
    document.title = "Malayalam.Ai - Admin";
    return () => {
      document.title = originalTitle;
    };
  }, []);
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [malayalamMenuOpen, setMalayalamMenuOpen] = useState(true);
  const [chatroomMenuOpen, setChatroomMenuOpen] = useState(false);
  const [miscMenuOpen, setMiscMenuOpen] = useState(false);

  // Set admin page title
  useAdminTitle();

  // Derive active section from URL path
  const getActiveSection = () => {
    const path = location.pathname;
    // Core (Malayalam.Ai)
    if (path.includes("/core/users")) return "users";
    // Chatroom
    if (path.includes("/chatroom/overview")) return "chatroom-overview";
    if (path.includes("/chatroom/rooms")) return "rooms";
    if (path.includes("/chatroom/user/")) return "user-detail";
    if (path.includes("/chatroom/guests/fingerprinting")) return "fingerprinting";
    if (path.includes("/chatroom/guests")) return "guests";
    if (path.includes("/chatroom/messages")) return "messages";
    if (path.includes("/chatroom/username-words")) return "username-words";
    if (path.match(/\/chatroom\/ip\/[^/]+\/users/)) return "ip-users";
    if (path.includes("/chatroom/ip")) return "ip-addresses";
    if (path.includes("/chatroom/reports")) return "reports";
    if (path.includes("/chatroom/bans")) return "bans";
    if (path.includes("/chatroom/settings")) return "chatroom-settings";
    // Content
    if (path.includes("/dashboard/media")) return "media";
    if (path.includes("/dashboard/blog")) return "blog";
    // System
    if (path.includes("/database")) return "database";
    if (path.includes("/analytics")) return "analytics";
    if (path.includes("/dashboard/settings")) return "settings";
    return "overview";
  };

  const activeSection = getActiveSection();

  // Extract username from URL for user-detail page
  const getUsernameFromPath = () => {
    const match = location.pathname.match(/\/chatroom\/user\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  };
  const usernameFromPath = getUsernameFromPath();

  // Extract IP address from URL for ip-users page
  const getIPAddressFromPath = () => {
    const match = location.pathname.match(/\/chatroom\/ip\/([^/]+)\/users/);
    return match ? decodeURIComponent(match[1]) : null;
  };
  const ipAddressFromPath = getIPAddressFromPath();

  // Check if any submenu is active
  const isMalayalamSection = ["users"].includes(activeSection);
  const isChatroomSection = ["chatroom-overview", "rooms", "guests", "fingerprinting", "user-detail", "messages", "username-words", "ip-addresses", "ip-users", "reports", "bans", "chatroom-settings"].includes(activeSection);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/admin/me`
        );
        setAdmin(response.data.admin);
      } catch (err) {
        navigate("/admin");
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/logout`);
      navigate("/admin");
    } catch (err) {
      console.error("Logout error:", err);
      navigate("/admin");
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const mainMenuItems = [
    { text: "Overview", icon: <DashboardIcon />, section: "overview", path: "/admin/dashboard" },
  ];

  const malayalamSubMenuItems = [
    { text: "Users & Guests", icon: <PeopleIcon />, section: "users", path: "/admin/core/users" },
  ];

  const chatroomSubMenuItems = [
    { text: "Overview", icon: <DashboardIcon />, section: "chatroom-overview", path: "/admin/chatroom/overview" },
    { text: "ChatRooms", icon: <MeetingRoomIcon />, section: "rooms", path: "/admin/chatroom/rooms" },
    { text: "ChatGuests", icon: <PersonOutlineIcon />, section: "guests", path: "/admin/chatroom/guests" },
    { text: "ChatMessages", icon: <MessageIcon />, section: "messages", path: "/admin/chatroom/messages" },
    { text: "ChatUsernameWords", icon: <TextFieldsIcon />, section: "username-words", path: "/admin/chatroom/username-words" },
    { text: "ChatIPAddresses", icon: <RouterIcon />, section: "ip-addresses", path: "/admin/chatroom/ip" },
    { text: "ChatReports", icon: <ReportIcon />, section: "reports", path: "/admin/chatroom/reports" },
    { text: "ChatBans", icon: <BlockIcon />, section: "bans", path: "/admin/chatroom/bans" },
    { text: "ChatSettings", icon: <TuneIcon />, section: "chatroom-settings", path: "/admin/chatroom/settings" },
  ];

  const bottomMenuItems = [
    { text: "Media", icon: <ImageIcon />, section: "media", path: "/admin/dashboard/media" },
    { text: "Blog", icon: <ArticleIcon />, section: "blog", path: "/admin/dashboard/blog" },
    { text: "Database", icon: <StorageIcon />, section: "database", path: "/admin/dashboard/database" },
    { text: "Analytics", icon: <BarChartIcon />, section: "analytics", path: "/admin/dashboard/analytics" },
    { text: "Settings", icon: <SettingsIcon />, section: "settings", path: "/admin/dashboard/settings" },
  ];

  const allMenuItems = [...mainMenuItems, ...malayalamSubMenuItems, ...chatroomSubMenuItems, ...bottomMenuItems];

  const drawer = (
    <Box sx={{ backgroundColor: "#16213e", height: "100%", display: "flex", flexDirection: "column" }}>
      <Toolbar>
        <Typography variant="h6" sx={{ color: "#fff", fontWeight: 600 }}>
          Admin Panel
        </Typography>
      </Toolbar>
      <Divider sx={{ borderColor: "#394867" }} />

      <List sx={{ flex: 1 }}>
        {/* Main Menu Items */}
        {mainMenuItems.map((item) => (
          <ListItemButton
            component={Link}
            to={item.path}
            key={item.text}
            sx={{
              backgroundColor: activeSection === item.section ? "#541b14" : "transparent",
              "&:hover": { backgroundColor: "#394867" },
              my: 0.5,
              mx: 1,
              borderRadius: 1,
              textDecoration: "none",
            }}
          >
            <ListItemIcon sx={{ color: "#fff", minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              sx={{ "& .MuiTypography-root": { color: "#fff" } }}
            />
          </ListItemButton>
        ))}

        {/* Malayalam.Ai Mgmt Collapsible */}
        <ListItemButton
          onClick={() => setMalayalamMenuOpen(!malayalamMenuOpen)}
          sx={{
            backgroundColor: isMalayalamSection ? "rgba(84, 27, 20, 0.3)" : "transparent",
            "&:hover": { backgroundColor: "#394867" },
            my: 0.5,
            mx: 1,
            borderRadius: 1,
          }}
        >
          <ListItemIcon sx={{ color: isMalayalamSection ? "#4caf50" : "#fff", minWidth: 40 }}>
            <LanguageIcon />
          </ListItemIcon>
          <ListItemText
            primary="Malayalam.Ai"
            sx={{ "& .MuiTypography-root": { color: isMalayalamSection ? "#4caf50" : "#fff", fontWeight: isMalayalamSection ? 600 : 400 } }}
          />
          {malayalamMenuOpen ? <ExpandLess sx={{ color: "#9e9e9e" }} /> : <ExpandMore sx={{ color: "#9e9e9e" }} />}
        </ListItemButton>

        <Collapse in={malayalamMenuOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {malayalamSubMenuItems.map((item) => (
              <ListItemButton
                component={Link}
                to={item.path}
                key={item.text}
                sx={{
                  backgroundColor: activeSection === item.section ? "#541b14" : "transparent",
                  "&:hover": { backgroundColor: "#394867" },
                  my: 0.25,
                  mx: 1,
                  ml: 3,
                  borderRadius: 1,
                  py: 0.75,
                  textDecoration: "none",
                }}
              >
                <ListItemIcon sx={{ color: "#9e9e9e", minWidth: 32 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{ "& .MuiTypography-root": { color: "#fff", fontSize: "0.9rem" } }}
                />
              </ListItemButton>
            ))}
          </List>
        </Collapse>

        {/* Divider before system items */}
        <Divider sx={{ borderColor: "#394867", my: 1.5, mx: 1 }} />

        {/* Bottom Menu Items */}
        {bottomMenuItems.map((item) => (
          <ListItemButton
            component={Link}
            to={item.path}
            key={item.text}
            sx={{
              backgroundColor: activeSection === item.section ? "#541b14" : "transparent",
              "&:hover": { backgroundColor: "#394867" },
              my: 0.5,
              mx: 1,
              borderRadius: 1,
              textDecoration: "none",
            }}
          >
            <ListItemIcon sx={{ color: "#fff", minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              sx={{ "& .MuiTypography-root": { color: "#fff" } }}
            />
          </ListItemButton>
        ))}
        {/* Misc Collapsible (Chatroom etc.) */}
        <Divider sx={{ borderColor: "#394867", my: 1.5, mx: 1 }} />
        <ListItemButton
          onClick={() => setMiscMenuOpen(!miscMenuOpen)}
          sx={{
            backgroundColor: isChatroomSection ? "rgba(84, 27, 20, 0.3)" : "transparent",
            "&:hover": { backgroundColor: "#394867" },
            my: 0.5, mx: 1, borderRadius: 1,
          }}
        >
          <ListItemIcon sx={{ color: isChatroomSection ? "#ff9800" : "#9e9e9e", minWidth: 40 }}>
            <ForumIcon />
          </ListItemIcon>
          <ListItemText primary="Misc" sx={{ "& .MuiTypography-root": { color: "#9e9e9e", fontSize: "0.9rem" } }} />
          {miscMenuOpen ? <ExpandLess sx={{ color: "#616161" }} /> : <ExpandMore sx={{ color: "#616161" }} />}
        </ListItemButton>

        <Collapse in={miscMenuOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {chatroomSubMenuItems.map((item) => (
              <ListItemButton
                component={Link}
                to={item.path}
                key={item.text}
                sx={{
                  backgroundColor: activeSection === item.section ? "#541b14" : "transparent",
                  "&:hover": { backgroundColor: "#394867" },
                  my: 0.25, mx: 1, ml: 3, borderRadius: 1, py: 0.75, textDecoration: "none",
                }}
              >
                <ListItemIcon sx={{ color: "#9e9e9e", minWidth: 32 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} sx={{ "& .MuiTypography-root": { color: "#fff", fontSize: "0.9rem" } }} />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      </List>

      <Divider sx={{ borderColor: "#394867" }} />
      <List>
        {process.env.NODE_ENV !== "production" && (
          <ListItem
            button
            onClick={() => {
              const currentPath = window.location.pathname + window.location.search;
              window.open(`https://malayalam.ai${currentPath}`, "_blank");
            }}
            sx={{
              "&:hover": { backgroundColor: "#394867" },
              mx: 1,
              mb: 0.5,
              borderRadius: 1,
            }}
          >
            <ListItemIcon sx={{ color: "#ff9800", minWidth: 40 }}>
              <OpenInNewIcon />
            </ListItemIcon>
            <ListItemText
              primary="Open in Prod"
              sx={{ "& .MuiTypography-root": { color: "#ff9800", fontSize: "0.9rem" } }}
            />
          </ListItem>
        )}
        <ListItem
          button
          onClick={handleLogout}
          sx={{
            "&:hover": { backgroundColor: "#394867" },
            mx: 1,
            mb: 1,
            borderRadius: 1,
          }}
        >
          <ListItemIcon sx={{ color: "#ef5350", minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            sx={{ "& .MuiTypography-root": { color: "#ef5350" } }}
          />
        </ListItem>
      </List>
    </Box>
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#1a1a2e",
        }}
      >
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: "#16213e",
          boxShadow: "none",
          borderBottom: "1px solid #394867",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            {allMenuItems.find((item) => item.section === activeSection)?.text ||
              "Dashboard"}
          </Typography>
          <Typography variant="body2" sx={{ color: "#9e9e9e", mr: 2 }}>
            Welcome, {admin?.username}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{
              color: "#ef5350",
              borderColor: "#ef5350",
              "&:hover": {
                borderColor: "#ef5350",
                backgroundColor: "rgba(239, 83, 80, 0.1)",
              },
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "#16213e",
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "#16213e",
              borderRight: "1px solid #394867",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: "#1a1a2e",
          minHeight: "100vh",
        }}
      >
        <Toolbar />
        <Container maxWidth={false} sx={{ maxWidth: "100%", px: 3 }}>
          {activeSection === "overview" && (
            <OverviewSection admin={admin} />
          )}
          {/* Core (Malayalam.Ai) */}
          {activeSection === "users" && <UsersGuestsSection />}
          {/* Chatroom */}
          {activeSection === "chatroom-overview" && <ChatroomOverviewSection />}
          {activeSection === "rooms" && <ChatRoomsSection />}
          {activeSection === "guests" && <ChatGuestsSection />}
          {activeSection === "fingerprinting" && <ChatFingerprintingSection />}
          {activeSection === "user-detail" && usernameFromPath && <ChatUserDetailSection username={usernameFromPath} />}
          {activeSection === "messages" && <ChatMessagesSection />}
          {activeSection === "username-words" && <UsernameWordsSection />}
          {activeSection === "ip-addresses" && <ChatIPAddressesSection />}
          {activeSection === "ip-users" && ipAddressFromPath && <ChatIPUsersSection ipAddress={ipAddressFromPath} />}
          {activeSection === "reports" && <ChatReportsSection />}
          {activeSection === "bans" && <PlaceholderSection description="Manage IP, fingerprint, and username bans" />}
          {activeSection === "chatroom-settings" && <ChatSettingsSection />}
          {/* Content */}
          {activeSection === "media" && <MediaSection />}
          {activeSection === "blog" && <BlogSection />}
          {/* System */}
          {activeSection === "database" && <DatabaseSection />}
          {activeSection === "analytics" && <PlaceholderSection />}
          {activeSection === "settings" && <PlaceholderSection />}
        </Container>
      </Box>
    </Box>
  );
};

const OverviewSection = ({ admin }) => {
  return (
    <Box>
      <Grid container spacing={3}>
        {/* Admin Info Card */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              backgroundColor: "#16213e",
              borderRadius: 2,
              border: "1px solid #394867",
            }}
          >
            <Typography variant="h6" sx={{ color: "#9e9e9e", mb: 2 }}>
              Admin Info
            </Typography>
            <Typography variant="body1" sx={{ color: "#fff", mb: 1 }}>
              <strong>Username:</strong> {admin?.username}
            </Typography>
            <Typography variant="body1" sx={{ color: "#fff", mb: 1 }}>
              <strong>Role:</strong> {admin?.role}
            </Typography>
            <Typography variant="body1" sx={{ color: "#fff", mb: 1 }}>
              <strong>Email:</strong> {admin?.email || "Not set"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#9e9e9e", mt: 2 }}>
              Last login:{" "}
              {admin?.lastLoginAt
                ? new Date(admin.lastLoginAt).toLocaleString()
                : "N/A"}
            </Typography>
          </Paper>
        </Grid>

        {/* Quick Stats Placeholder */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              backgroundColor: "#16213e",
              borderRadius: 2,
              border: "1px solid #394867",
              height: "100%",
            }}
          >
            <Typography variant="h6" sx={{ color: "#9e9e9e", mb: 2 }}>
              Quick Stats
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", fontStyle: "italic" }}>
              Stats will be populated here...
            </Typography>
          </Paper>
        </Grid>

        {/* Recent Activity Placeholder */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              backgroundColor: "#16213e",
              borderRadius: 2,
              border: "1px solid #394867",
              height: "100%",
            }}
          >
            <Typography variant="h6" sx={{ color: "#9e9e9e", mb: 2 }}>
              Recent Activity
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", fontStyle: "italic" }}>
              Activity feed will appear here...
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* System Status */}
      <Paper
        sx={{
          p: 3,
          mt: 3,
          backgroundColor: "#16213e",
          borderRadius: 2,
          border: "1px solid #394867",
        }}
      >
        <Typography variant="h6" sx={{ color: "#9e9e9e", mb: 2 }}>
          System Status
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "#1a1a2e",
                borderRadius: 1,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
                Main App
              </Typography>
              <Typography variant="h6" sx={{ color: "#4caf50" }}>
                Online
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "#1a1a2e",
                borderRadius: 1,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
                Database
              </Typography>
              <Typography variant="h6" sx={{ color: "#4caf50" }}>
                Connected
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "#1a1a2e",
                borderRadius: 1,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
                Chatroom
              </Typography>
              <Typography variant="h6" sx={{ color: "#ff9800" }}>
                Not Started
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "#1a1a2e",
                borderRadius: 1,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
                API
              </Typography>
              <Typography variant="h6" sx={{ color: "#4caf50" }}>
                Healthy
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

const PlaceholderSection = ({ description }) => {
  return (
    <Box>
      <Paper
        sx={{
          p: 4,
          backgroundColor: "#16213e",
          borderRadius: 2,
          border: "1px solid #394867",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" sx={{ color: "#666" }}>
          This section is under construction
        </Typography>
        <Typography variant="body2" sx={{ color: "#555", mt: 1 }}>
          {description || "Features will be added here soon..."}
        </Typography>
      </Paper>
    </Box>
  );
};

export default AdminDashboard;
